import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { ICase } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { cancelCaseAvailability, getFriendlyNameOfCaseType } from '@brands/Utils/getFriendlyName';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import {
  cancelCase,
  CancelReasonCode,
  PatientCancelReasonCode,
  StaffCancelReasonCode,
} from '../../../../services/cases/cancelCase';
import styles from './styles.module.scss';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';

interface IModal {
  setOpenModal: (arg0: boolean) => void;
  thisCase: ICase;
  reloadAll: () => Promise<void>;
  refreshAll: () => Promise<void>;
}

const CancelAppointmentModal = ({ setOpenModal, thisCase, reloadAll, refreshAll }: IModal): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const roleName = (userInfo?.role?.name || 'Patient') as string;

  const patientOptions: { label: string; value: PatientCancelReasonCode }[] = [
    { label: 'I am no longer available', value: 'NO_LONGER_AVAILABLE' },
    { label: 'I no longer need help', value: 'NO_LONGER_NEEDS_HELP' },
    { label: 'I received help elsewhere', value: 'RECEIVED_HELP_ELSEWHERE' },
    { label: 'I am having platform problems', value: 'PLATFORM_PROBLEMS' },
    { label: 'I accidentally requested multiple cases', value: 'ACCIDENTAL_MULTIPLE_CASES' },
    { label: 'Other', value: 'OTHER' },
  ];

  const staffOptions: { label: string; value: StaffCancelReasonCode }[] = [
    { label: 'The patient is no longer available', value: 'PATIENT_NO_LONGER_AVAILABLE' },
    { label: 'The patient no longer needs help', value: 'PATIENT_NO_LONGER_NEEDS_HELP' },
    { label: 'The patient received help elsewhere', value: 'PATIENT_RECEIVED_HELP_ELSEWHERE' },
    { label: 'The patient is having platform problems', value: 'PATIENT_PLATFORM_PROBLEMS' },
    { label: 'The patient accidentally requested multiple cases', value: 'PATIENT_ACCIDENTAL_MULTIPLE_CASES' },
    { label: "The patient didn't attend the appointment", value: 'PATIENT_DID_NOT_ATTEND' },
    { label: "The provider didn't attend the appointment", value: 'PROVIDER_DID_NOT_ATTEND' },
    { label: 'Other', value: 'OTHER' },
  ];

  const isStaff = useMemo(
    () => [UserRoleName.CareAdvisor, UserRoleName.OrganizationAdmin, UserRoleName.Admin, UserRoleName.SuperAdmin].includes(roleName),
    [roleName],
  );

  const options = isStaff ? staffOptions : patientOptions;

  const caseTypeLabel = getFriendlyNameOfCaseType(thisCase.type) || 'appointment';

  const [step, setStep] = useState<1 | 2>(1);
  const [selectedReason, setSelectedReason] = useState<CancelReasonCode | ''>('');
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const needsOtherText = selectedReason === 'OTHER';
  const canContinue = selectedReason !== '' && (!needsOtherText || otherText.trim().length > 0);

  const onContinue = (): void => {
    if (!canContinue) {
      toast.error('Please select a reason to continue.');
      return;
    }
    setStep(2);
  };

  const doCancel = async (): Promise<void> => {
    if (!cancelCaseAvailability(thisCase)) {
      toast.error('You cannot cancel this case.');
      setOpenModal(false);
      return;
    }
    try {
      setSubmitting(true);
      await cancelCase(thisCase.id, {
        reason_code: selectedReason as CancelReasonCode,
        reason_other: needsOtherText ? otherText.trim() : undefined,
      });
      await reloadAll();
      await refreshAll();
      toast.success('Appointment canceled.');
      setOpenModal(false);
    } catch (e) {
      displayErrorDetails(e);
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
      <div className={`${styles.modalCard} ${step === 1 ? styles.stepOne : styles.stepTwo}`} key={step}>
        {/* close (X) */}
        <button aria-label="Close" className={styles.close} onClick={() => setOpenModal(false)} type="button">
          ×
        </button>

        {step === 1 ? (
          <>
            <h3 className={`${styles.heading} fs-unmask`}>
              Why do you want to cancel your {caseTypeLabel.toLowerCase()}?
            </h3>

            <div className={styles.list} role="radiogroup" aria-label="Cancel reason">
              {options.map((opt) => (
                <label key={opt.value} className={styles.option}>
                  <input
                    type="radio"
                    name="cancel_reason"
                    value={opt.value}
                    checked={selectedReason === opt.value}
                    onChange={() => setSelectedReason(opt.value)}
                  />
                  <span className="fs-unmask">{opt.label}</span>
                </label>
              ))}
            </div>

            {needsOtherText && (
              <div className={styles.otherWrap}>
                <TextField
                  name="other"
                  multiline
                  minRows={4}
                  value={otherText}
                  label="Share your feedback"
                  errors={[]}
                  onChange={(val) => setOtherText(val.target.value)}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                />
              </div>
            )}

            <div className={styles.actions}>
              <button className={styles.primary} type="button" disabled={!canContinue} onClick={onContinue}>
                Continue to Cancel
              </button>
              <button className={styles.link} type="button" onClick={() => setOpenModal(false)}>
                Keep Service
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className={`${styles.headingCenter} fs-unmask`}>
              Are you sure you want to cancel your {caseTypeLabel.toLowerCase()}?
            </h3>

            <div className={styles.actionsCentered}>
              <button className={styles.danger} type="button" disabled={submitting} onClick={doCancel}>
                Cancel Appointment
              </button>
              <button className={styles.link} type="button" onClick={() => setOpenModal(false)}>
                Keep Service
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CancelAppointmentModal;
